import { useState, useCallback } from 'react';
import { 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  query, 
  orderBy, 
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Prayer, DailyPrayers, UserLocation } from '../types';

// Use local date (not UTC) to avoid WIB/UTC off-by-one issues
export function getLocalDateString(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Firestore rejects undefined values - strip them completely
function sanitizePrayer(prayer: Prayer): Record<string, unknown> {
  const raw: Record<string, unknown> = {
    name: prayer.name,
    scheduledTime: prayer.scheduledTime,
    actualTime: prayer.actualTime ?? null,
    completed: prayer.completed,
    location: prayer.location ?? '',
    coordinates: prayer.coordinates,
    surahFirst: prayer.surahFirst,
    surahFirstVerseStart: prayer.surahFirstVerseStart,
    surahFirstVerseEnd: prayer.surahFirstVerseEnd,
    surahFirstMemory: prayer.surahFirstMemory,
    surahSecond: prayer.surahSecond,
    surahSecondVerseStart: prayer.surahSecondVerseStart,
    surahSecondVerseEnd: prayer.surahSecondVerseEnd,
    surahSecondMemory: prayer.surahSecondMemory,
    prayerStatus: prayer.prayerStatus,
    jamaahRole: prayer.jamaahRole,
    travelStatus: prayer.travelStatus,
    jamaType: prayer.jamaType,
    qasharType: prayer.qasharType,
    jamaDirection: prayer.jamaDirection,
    healthStatus: prayer.healthStatus,
    sickPosition: prayer.sickPosition,
    timeStatus: prayer.timeStatus,
  };
  // Remove keys with undefined (Firestore doesn't allow undefined)
  Object.keys(raw).forEach(k => {
    if (raw[k] === undefined) delete raw[k];
  });
  return raw;
}

export function useFirestore(userId: string | null) {
  const [loading, setLoading] = useState(false);

  const saveUserData = useCallback(async (data: {
    prayers: Prayer[];
    userLocation?: UserLocation;
    language: string;
    theme?: string;
    date?: string;
    notificationPrefs?: { enabled: boolean; beforeMinutes: number; skipCompleted?: boolean; sound?: string };
  }) => {
    if (!userId) return;

    try {
      setLoading(true);
      const targetDate = data.date || getLocalDateString();
      
      // Create a clean copy - Firestore tidak boleh ada undefined
      const cleanPrayers = data.prayers.map(sanitizePrayer);
      
      // Save daily prayers for specific date
      const prayerDocRef = doc(db, 'users', userId, 'dailyPrayers', targetDate);
      await setDoc(prayerDocRef, {
        date: targetDate,
        prayers: cleanPrayers,
        updatedAt: Timestamp.now(),
        version: Date.now()
      });

      // Save user preferences (only if not saving historical data)
      if (!data.date) {
        const userDocRef = doc(db, 'users', userId);
        const pref: Record<string, unknown> = {
          language: data.language,
          theme: data.theme,
          lastUpdated: Timestamp.now()
        };
        if (data.userLocation !== undefined) pref.userLocation = data.userLocation;
        if (data.notificationPrefs !== undefined) pref.notificationPrefs = data.notificationPrefs;
        // Strip undefined prefs too
        Object.keys(pref).forEach(k => { if (pref[k] === undefined) delete pref[k]; });
        await setDoc(userDocRef, pref as any, { merge: true });
      }

      console.log('Data saved successfully to Firestore for date:', targetDate);
    } catch (error) {
      console.error('Error saving user data:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadUserData = useCallback(async () => {
    if (!userId) return null;

    try {
      setLoading(true);
      
      // Load user preferences
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.exists() ? userDoc.data() : null;

      // Load today's prayers - use local date
      const today = getLocalDateString();
      const todayDoc = await getDoc(doc(db, 'users', userId, 'dailyPrayers', today));
      const todayData = todayDoc.exists() ? todayDoc.data() : null;

      console.log('Data loaded from Firestore:', { 
        hasUserData: !!userData, 
        hasTodayData: !!todayData,
        todayPrayersCount: todayData?.prayers?.length || 0,
        todayDate: today,
        todayPrayers: todayData?.prayers || []
      });

      return {
        userLocation: userData?.userLocation,
        language: userData?.language || 'en',
        theme: userData?.theme || 'emerald',
        notificationPrefs: userData?.notificationPrefs,
        todayPrayers: todayData?.prayers || []
      };
    } catch (error) {
      console.error('Error loading user data:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadPrayerHistory = useCallback((callback: (history: DailyPrayers[]) => void) => {
    if (!userId) return () => {};

    const q = query(
      collection(db, 'users', userId, 'dailyPrayers'),
      orderBy('date', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const history: DailyPrayers[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.prayers && Array.isArray(data.prayers)) {
          history.push({
            date: data.date,
            prayers: data.prayers
          });
        }
      });
      console.log('Prayer history loaded:', history.length, 'entries');
      callback(history);
    }, (error) => {
      console.error('Error loading prayer history:', error);
    });
  }, [userId]);

  const loadPrayersForDate = useCallback(async (date: string): Promise<Prayer[] | null> => {
    if (!userId) return null;

    try {
      const dateDoc = await getDoc(doc(db, 'users', userId, 'dailyPrayers', date));
      if (dateDoc.exists()) {
        const data = dateDoc.data();
        console.log('Prayers loaded for date:', date, 'count:', data.prayers?.length || 0, 'prayers:', data.prayers);
        return data.prayers && data.prayers.length > 0 ? data.prayers : null;
      }
      console.log('No prayers found for date:', date);
      return null;
    } catch (error) {
      console.error('Error loading prayers for date:', error);
      return null;
    }
  }, [userId]);

  // Force save function for immediate data persistence
  const forceSave = useCallback(async (prayers: Prayer[], date?: string) => {
    if (!userId) return;

    try {
      const targetDate = date || getLocalDateString();
      
      // Firestore tidak boleh ada undefined
      const cleanPrayers = prayers.map(sanitizePrayer);
      
      const docRef = doc(db, 'users', userId, 'dailyPrayers', targetDate);
      await setDoc(docRef, {
        date: targetDate,
        prayers: cleanPrayers,
        updatedAt: Timestamp.now(),
        version: Date.now()
      });

      console.log('Force save completed for date:', targetDate, 'prayers count:', cleanPrayers.length, 'prayers:', cleanPrayers);
    } catch (error) {
      console.error('Error in force save:', error);
      throw error;
    }
  }, [userId]);

  return {
    loading,
    saveUserData,
    loadUserData,
    loadPrayerHistory,
    loadPrayersForDate,
    forceSave
  };
}